/**
 * Landing page copy that states what exists. Every item lists the requirements
 * that deliver it, and src/test/landingAvailability.test.ts fails when an item
 * marked `available` cites a requirement whose Delivery in specs/ is not
 * `implemented` or `verified` (DASH-041). Change a status only by delivering
 * the work. No testimonials, logos, usage figures or certifications belong
 * here (DASH-057), and no AI provider or model name (CONT-058). Prices live in
 * PLAN_CATALOGUE and the pricing copy in src/billing/content.ts (BILL-010, BILL-102).
 */
import { STAGE_LABEL, STAGE_ORDER, type StageKey } from '../review/journey/labels';

export type Availability = 'available' | 'coming_soon';

export const AVAILABILITY_LABEL: Record<Availability, string> = {
  available: 'Available now',
  coming_soon: 'Coming soon',
};

export interface LandingItem {
  name: string;
  detail: string;
  status: Availability;
  /** Requirement IDs whose delivery makes this item true (DASH-041). At least one. */
  requirements: readonly string[];
}

const JOURNEY_COPY: Record<StageKey, Omit<LandingItem, 'name'>> = {
  question: {
    detail: 'Write the question in plain words. AI drafts the type of review, the eligible study designs, the question’s fields, outcomes and eligibility rules for you to edit.',
    status: 'available',
    requirements: ['QB-010', 'QB-013', 'QB-050', 'QB-136'],
  },
  protocol: {
    detail: 'Check the analysis settings and freeze the protocol. It is stored with a hash and can never be edited again.',
    status: 'available',
    requirements: ['QB-080', 'QB-084', 'QB-091'],
  },
  search: {
    detail: 'AI drafts a strategy for each source. Nothing runs until an owner approves it.',
    status: 'available',
    requirements: ['SRCH-020', 'SRCH-029', 'SRCH-033', 'SRCH-014'],
  },
  run: {
    detail: 'One run searches every source with its approved strategy, removes duplicates and screens what is new.',
    status: 'available',
    requirements: ['SRCH-035', 'SRCH-090', 'DEDUP-070'],
  },
  duplicates: {
    detail: 'Records that share an identifier or a near-identical citation are merged; uncertain pairs come to you.',
    status: 'available',
    requirements: ['DEDUP-020', 'DEDUP-024', 'DEDUP-034', 'DEDUP-067'],
  },
  screening: {
    detail: 'Two independent AI passes judge every title and abstract. You decide every record they disagree on.',
    status: 'available',
    requirements: ['SCRN-020', 'SCRN-032', 'SCRN-060'],
  },
  extraction: {
    detail: 'Full texts, each study’s design and the data of every outcome are extracted, each value tied to its quote, for a person to confirm; risk-of-bias judgements are signed off by a person.',
    status: 'coming_soon',
    requirements: ['EXTR-010', 'RBA-010'],
  },
  results: {
    detail: 'An evidence page that pools the studies in your browser and lets anyone interrogate the result.',
    status: 'available',
    requirements: ['PAGE-010', 'PAGE-021', 'PAGE-031'],
  },
};

/** The review journey in the app's own words and order (DASH-040; F23 STAGE_LABEL). */
export const JOURNEY: readonly LandingItem[] = STAGE_ORDER.map((key) => ({ name: STAGE_LABEL[key], ...JOURNEY_COPY[key] }));

/** The searched sources, in the order the search screens list them. */
export const SEARCH_SOURCES = 'PubMed, Europe PMC, ClinicalTrials.gov and OpenAlex';

export const ALERT_TRIGGERS: readonly string[] = [
  'a study is added',
  'the pooled estimate crosses the line of no effect',
  'the certainty rating changes',
  'an included study is retracted',
];

function sentenceList(items: readonly string[]): string {
  return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

/** What works today, in the order of DASH-043. */
export const FEATURES: readonly LandingItem[] = [
  {
    name: 'Protocol drafted from your question',
    detail:
      'Type the question as you would ask a colleague. AI drafts the type of review, the eligible study designs, the question fields, numbered eligibility rules and outcomes of each type, and asks about anything it cannot settle. You edit every line.',
    status: 'available',
    requirements: ['QB-010', 'QB-012', 'QB-050', 'QB-014', 'QB-136'],
  },
  {
    name: 'Every study design',
    detail:
      'Randomised trials, including cluster and crossover trials; non-randomised studies; cohort, case-control and cross-sectional studies; single-arm studies and case series; and prevalence and incidence studies. A question is framed as PICO, as PECO for an exposure, or by condition, context and population for prevalence, and the search filter, screening, risk-of-bias tool and GRADE starting level follow its eligible designs.',
    status: 'available',
    requirements: ['QB-130', 'QB-131', 'QB-132', 'SRCH-180', 'SCRN-101', 'EXTR-210', 'RBA-100', 'CERT-150', 'EVM-140'],
  },
  {
    name: 'Frozen protocol with a hash',
    detail:
      'Freezing records the protocol before any search runs. The SHA-256 hash is shown with the time, and frozen content cannot be changed, only duplicated as a new draft.',
    status: 'available',
    requirements: ['QB-080', 'QB-084', 'QB-088', 'QB-090', 'QB-091', 'QB-092'],
  },
  {
    name: 'Search strategies you approve',
    detail: `${SEARCH_SOURCES}, each with a strategy AI drafts and an owner approves. Approved versions are immutable, and every run logs its queries and hit counts. Exports from other databases can be uploaded.`,
    status: 'available',
    requirements: ['SRCH-040', 'SRCH-050', 'SRCH-060', 'SRCH-070', 'SRCH-033', 'SRCH-034', 'SRCH-090', 'SRCH-110'],
  },
  {
    name: 'Duplicate removal',
    detail:
      'DOIs, PubMed and PMC IDs and trial registry numbers are matched first, then near-identical citations. Conflicting identifiers go to a person, and every merge can be undone.',
    status: 'available',
    requirements: ['DEDUP-020', 'DEDUP-021', 'DEDUP-024', 'DEDUP-034', 'DEDUP-046', 'DEDUP-066'],
  },
  {
    name: 'Dual-pass AI screening',
    detail:
      'Two independent passes judge each record against a numbered eligibility rule and quote the words they relied on. A person decides every disagreement and checks a sample of agreed exclusions.',
    status: 'available',
    requirements: ['SCRN-020', 'SCRN-022', 'SCRN-032', 'SCRN-036', 'SCRN-062'],
  },
  {
    name: 'Full texts and data extraction',
    detail:
      'Full texts of included records are fetched from Europe PMC or Unpaywall when they are open access, or uploaded by you. AI drafts each study’s design and the data of each outcome, every number with the exact quote it came from; a number missing from its quote is dropped, and nothing extracted enters the default analysis until a person confirms or corrects it.',
    status: 'available',
    requirements: ['EXTR-011', 'EXTR-012', 'EXTR-013', 'EXTR-020', 'EXTR-053', 'EXTR-063', 'EXTR-074', 'EXTR-101', 'EXTR-102', 'EXTR-210'],
  },
  {
    name: 'Fuller data extraction',
    detail:
      'Every outcome type: events per group for binary outcomes, means with SDs for continuous outcomes (an SD from a printed standard error or confidence interval cites both inputs), hazard ratios and other reported estimates with what they are adjusted for, as observational studies give them, proportions and prevalence, incidence rates over person-time, and correlations. Every arm of multi-arm trials is kept, and arms are combined or a shared arm split as the protocol says.',
    status: 'available',
    requirements: ['EXTR-200', 'EXTR-202', 'EXTR-203', 'EXTR-205', 'EXTR-207', 'EXTR-212', 'EXTR-213', 'EXTR-214', 'EVM-142'],
  },
  {
    name: 'PRISMA counts',
    detail:
      'Records identified per source, duplicates removed, and records screened, excluded and included, for each run and in total, ready for the flow diagram.',
    status: 'available',
    requirements: ['SRCH-150', 'SRCH-151', 'DEDUP-073', 'SCRN-071', 'SCRN-075', 'SCRN-078'],
  },
  {
    name: 'Evidence pages you can interrogate',
    detail:
      'Switch the effect measure or model and the pooled estimate recalculates at once. Leave-one-out and influence, the funnel plot with Egger’s test at ten or more studies, cumulative meta-analysis, trial sequential analysis and pre-specified subgroups are a tab away.',
    status: 'available',
    requirements: ['PAGE-031', 'PAGE-101', 'PAGE-102', 'PAGE-105', 'PAGE-106', 'PAGE-110', 'PAGE-111', 'PAGE-121'],
  },
  {
    name: 'A source for every number',
    detail:
      'Open any study to see each extracted value next to the source it cites and its review status. Values extracted from a full text cite the exact quote they came from; today’s seed pages cite a row of the published dataset they come from.',
    status: 'available',
    requirements: ['EVM-020', 'PAGE-095', 'PAGE-096', 'EXTR-061', 'EXTR-077'],
  },
  {
    name: 'Risk of bias, signed off by a person',
    detail:
      'For randomised trials, AI answers the RoB 2 signalling questions with quotes checked against the full text, and the domain and overall judgements follow the RoB 2 algorithm. Non-randomised studies of interventions are assessed with ROBINS-I by a person. Only judgements a person signs off reach the analysis.',
    status: 'available',
    requirements: ['RBA-010', 'RBA-017', 'RBA-018', 'RBA-100', 'RBA-103'],
  },
  {
    name: 'Exports for your manuscript',
    detail:
      'The PRISMA 2020 flow diagram, the data as CSV with a data dictionary, RevMan Web import files, an R script that reproduces the analysis in metafor, a Word report draft and the decision log.',
    status: 'available',
    requirements: ['RPT-010', 'RPT-020', 'RPT-030', 'RPT-037', 'RPT-071', 'RPT-170'],
  },
  {
    name: 'Living updates',
    detail:
      'Turn on a schedule: weekly, monthly by default, or every 3 months. Each run searches again from the last search, screens new records and drafts data for new studies. A snapshot is recorded only when the confirmed evidence changes, and new studies wait for a reviewer before they change the published estimate.',
    status: 'available',
    requirements: ['LIVE-010', 'LIVE-012', 'LIVE-021', 'LIVE-062', 'LIVE-063', 'LIVE-071', 'LIVE-091', 'LIVE-095'],
  },
  {
    name: 'Alerts',
    detail: `Follow a page and be told when ${sentenceList(ALERT_TRIGGERS)}, in Bioterafi and, if you choose, by email.`,
    status: 'available',
    // Following (ALRT-010), alerts from snapshots (ALRT-020), the Follow button (ALRT-044), the alerts page (ALRT-046)
    // and alert email (ALRT-063), all delivered by F19 (18 Oct 2026).
    requirements: ['ALRT-010', 'ALRT-020', 'ALRT-044', 'ALRT-046', 'ALRT-063'],
  },
];

/** Specified and not yet built, in the order of DASH-043. */
export const COMING_SOON: readonly LandingItem[] = [
  {
    name: 'Study details and table cells',
    detail: 'Values read from table cells, and study details such as countries, setting, funding, doses and analysis populations, each tied to its source.',
    status: 'coming_soon',
    requirements: ['EXTR-052', 'EXTR-110', 'EXTR-120', 'EXTR-044'],
  },
];

/** DASH-045. */
export const RESEARCHER_POINTS: readonly LandingItem[] = [
  {
    name: 'An audit trail with names on it',
    detail:
      'Every save, freeze, strategy approval, duplicate decision and screening decision is written to the audit log with who made it and when. Sign in with ORCID and your account carries your ORCID iD.',
    status: 'available',
    requirements: ['QB-064', 'QB-083', 'SRCH-033', 'DEDUP-066', 'SCRN-047', 'DASH-023'],
  },
  {
    name: 'Every AI decision recorded',
    detail:
      'Each AI draft, screening pass and extracted value is stored with the model that produced it and the time, and written to the audit log with the model as the actor.',
    status: 'available',
    requirements: ['AI-060', 'AI-061', 'SCRN-027', 'QB-012', 'EXTR-072'],
  },
  {
    name: 'No value without a source',
    detail:
      'Every extracted number cites the source it came from. Values that nobody has confirmed stay out of the default analysis, and are marked when you choose to include them.',
    status: 'available',
    requirements: ['EVM-020', 'PAGE-034'],
  },
];

export interface FaqItem {
  question: string;
  answer: readonly string[];
}

/** DASH-157. Each answer describes delivered behaviour only. */
export const FAQ: readonly FaqItem[] = [
  {
    question: 'Is Bioterafi clinical advice?',
    answer: [
      'No. It is a research and evidence synthesis tool, not clinical advice, and it must not be used to make decisions about an individual patient’s care.',
      'Bioterafi is for researchers, reviewers and guideline groups synthesising evidence from randomised trials, non-randomised studies, cohort, case-control and cross-sectional studies, case series and prevalence studies. Its estimates depend on the studies found and on extracted values that people confirm; read every page with its risk-of-bias and certainty information, and check the sources before relying on a number.',
    ],
  },
  {
    question: 'Which databases does it search?',
    answer: [
      `${SEARCH_SOURCES}. Each source runs the strategy an owner approved, and every run records the exact queries, the date and the hit counts, so the search can be reported and repeated.`,
      'You can also upload RIS, MEDLINE or CSV exports from other databases, with the strategy you ran there.',
    ],
  },
  {
    question: 'What does the AI do, and what does it not do?',
    answer: [
      'It drafts: the type of review, the eligible study designs, the question’s fields (population, intervention or exposure, comparator or context, and outcomes) and eligibility rules; a search strategy for each source; two independent screening judgements for each record, each citing a numbered rule and a quote; and, from the full text, each study’s design and the data of each outcome, each with the quote it came from.',
      'People decide: you edit and freeze the protocol, an owner approves every strategy before it runs, a person decides every record the two passes disagree on or are unsure about, and a person confirms or corrects every extracted number before it is pooled. The AI never computes a pooled estimate; the statistics engine does, in your browser.',
    ],
  },
  {
    question: 'Is my review private?',
    answer: [
      'Questions and private evidence pages belong to your workspace. Anyone outside it gets the same “not found” answer as for something that does not exist. A page you share can be read by anyone and changed only by your workspace’s owners and editors.',
      'To draft, screen and extract, your question, its protocol, the titles and abstracts of the records found and the full texts retrieved or uploaded for extraction are sent to our AI service, and each call is recorded.',
    ],
  },
];

/** The seed page used for the landing figure and the "see an example" link. */
export const EXAMPLE_SLUG = 'streptokinase-mi';

/** Where a new reviewer starts a review once signed in: the question builder (DASH-031). */
export const START_PATH = '/questions/new';
